import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  FileText,
  Info,
  AlertTriangle,
  Lock,
  Globe,
  BookOpen,
} from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Terms() {
  const { t } = useTranslation();
  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, delay: i * 0.15 },
    }),
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="relative py-20 bg-black">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
          >
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-3">
              {t("terms.hero.title1")}{" "}
              <span className="text-electric-blue">
                {t("terms.hero.highlight")}
              </span>
            </h1>

            <p className="font-paragraph text-white/80 max-w-xl mx-auto">
              {t("terms.hero.description")}
            </p>
            <FileText className="mx-auto mt-6 h-12 w-12 text-white opacity-70" />
          </motion.div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 mb-10"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          <div className="flex items-center gap-3 mb-3">
            <BookOpen className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-xl font-bold">
              {t("terms.summary.title")}
            </h2>
          </div>

          <ul className="font-paragraph space-y-2 text-foreground/80">
            <li>
              <a href="#access" className="hover:text-primary">
                {t("terms.summary.access")}
              </a>
            </li>

            <li>
              <a href="#usage" className="hover:text-primary">
                {t("terms.summary.usage")}
              </a>
            </li>

            <li>
              <a href="#content" className="hover:text-primary">
                {t("terms.summary.content")}
              </a>
            </li>

            <li>
              <a href="#responsability" className="hover:text-primary">
                {t("terms.summary.responsibility")}
              </a>
            </li>

            <li>
              <a href="#security" className="hover:text-primary">
                {t("terms.summary.security")}
              </a>
            </li>

            <li>
              <a href="#updates" className="hover:text-primary">
                {t("terms.summary.updates")}
              </a>
            </li>
          </ul>
        </motion.div>

        <motion.div
          className="bg-white rounded-xl shadow-lg p-8 space-y-10"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          <section id="access">
            <div className="flex items-center gap-3 mb-3">
              <Info className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-heading font-semibold">
                {t("terms.access.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("terms.access.description")}
            </p>
          </section>

          <section id="usage">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="h-6 w-6 text-secondary" />
              <h2 className="text-xl font-heading font-semibold">
                {t("terms.usage.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("terms.usage.description")}
            </p>

            <ul className="list-disc pl-6 mt-2 space-y-2 font-paragraph text-foreground/80">
              <li>{t("terms.usage.item1")}</li>
              <li>{t("terms.usage.item2")}</li>
              <li>{t("terms.usage.item3")}</li>
              <li>{t("terms.usage.item4")}</li>
              <li>{t("terms.usage.item5")}</li>
            </ul>
          </section>

          <section id="content">
            <div className="flex items-center gap-3 mb-3">
              <Globe className="h-6 w-6 text-electric-blue" />
              <h2 className="text-xl font-heading font-semibold">
                {t("terms.content.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("terms.content.description")}
            </p>
          </section>

          <section id="responsability">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-heading font-semibold">
                {t("terms.responsibility.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("terms.responsibility.description")}
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2 font-paragraph text-foreground/80">
              <li>{t("terms.responsibility.item1")}</li>
              <li>{t("terms.responsibility.item2")}</li>
              <li>{t("terms.responsibility.item3")}</li>
              <li>{t("terms.responsibility.item4")}</li>
            </ul>
          </section>

          <section id="security">
            <div className="flex items-center gap-3 mb-3">
              <Lock className="h-6 w-6 text-secondary" />
              <h2 className="text-xl font-heading font-semibold">
                {t("terms.security.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("terms.security.description")}
            </p>
          </section>

          <section id="updates">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-heading font-semibold">
                {t("terms.updates.title")}
              </h2>
            </div>

            <p className="font-paragraph text-foreground/80 leading-relaxed">
              {t("terms.updates.description")}
            </p>
          </section>
        </motion.div>

        <div className="text-center mt-10">
          <p className="text-sm text-gray-500 mb-4">{t("terms.lastUpdated")}</p>

          <Link
            to="/privacypolicy"
            className="inline-block font-paragraph text-primary hover:underline font-semibold"
          >
            {t("terms.privacyLink")}
          </Link>
        </div>
      </main>
    </div>
  );
}
